import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import { FsButton } from '~/components/core';
import TextField from '~/components/core/Molecules/Fields/TextField';

interface RenameChatDialogProps {
  open: boolean;
  currentTitle: string;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (newTitle: string) => Promise<void>;
}

const MAX_TITLE_LENGTH = 70;

export const RenameChatDialog: React.FC<RenameChatDialogProps> = ({
  open,
  currentTitle,
  isLoading,
  onClose,
  onSubmit,
}) => {
  const [title, setTitle] = useState(currentTitle);
  const [error, setError] = useState<string | null>(null);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);

    if (!newTitle.trim()) {
      setError('Name is required');
    } else if (newTitle.length > MAX_TITLE_LENGTH) {
      setError(`Name must be ${MAX_TITLE_LENGTH} characters or less`);
    } else {
      setError(null);
    }
  };

  const handleSubmit = async () => {
    await onSubmit(title.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !error && title.trim()) {
      handleSubmit();
    }
  };

  const footerActions = (
    <div className="flex w-full justify-end gap-12 px-16 pb-16">
      <FsButton variant="text" className="text-blue-600" onClick={onClose} disabled={isLoading}>
        Cancel
      </FsButton>
      <FsButton
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={isLoading || !!error || !title.trim()}
      >
        {isLoading ? 'Saving...' : 'Save'}
      </FsButton>
    </div>
  );

  return (
    <CardDialog
      isDialog
      open={open}
      title="Rename Chat"
      onClose={onClose}
      preventClose={isLoading}
      maxWidth="sm"
      fullWidth
      footerActions={footerActions}
    >
      <div className="pt-4">
        <TextField
          label="Name"
          size="medium"
          value={title}
          onChange={handleTitleChange}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error || `${title.length}/${MAX_TITLE_LENGTH}`}
          fullWidth
          autoFocus
          disabled={isLoading}
          inputProps={{ maxLength: MAX_TITLE_LENGTH + 10 }}
        />
      </div>
    </CardDialog>
  );
};
